import { useMemo } from "react";
import type { FilterState, ScopeFilters } from "@/types/filters";
import type { SchedulingRow } from "@/types/scheduling";
import type { ComputedMetrics } from "@/types/metrics";
import { peerScope } from "@/lib/drillScope";
import {
  applyBaseFilter,
  filterRowsByDateRange,
  filterRowsByScope,
  computeMetrics,
} from "@/lib/metrics";

export interface DrillRow {
  id: string;
  label: string;
  siteCount: number;
  metrics: ComputedMetrics;
}

export interface PinnedRow {
  label: string;
  siteCount: number;
  metrics: ComputedMetrics;
}

export const DRILL_LEVELS: FilterState["drillLevel"][] = [
  "country",
  "group",
  "division",
  "site",
  "dvm",
];

function groupRows(
  rows: SchedulingRow[],
  level: FilterState["drillLevel"]
): Map<string, { label: string; rows: SchedulingRow[] }> {
  const map = new Map<string, { label: string; rows: SchedulingRow[] }>();
  for (const row of rows) {
    let key: string;
    let label: string;
    switch (level) {
      case "country":
        key = row.country_code;
        label = row.country_code;
        break;
      case "division":
        key = row.division_name;
        label = row.division_name;
        break;
      case "group":
        key = row.group_name;
        label = row.group_name;
        break;
      case "site":
        key = row.location_id;
        label = `${row.location_id} - ${row.location_name}`;
        break;
      case "dvm":
        key = row.resource_id;
        label = row.resource_full_name;
        break;
    }
    if (!map.has(key)) map.set(key, { label, rows: [] });
    map.get(key)!.rows.push(row);
  }
  return map;
}

// Builds a scope that only includes filters up to and including the given ancestor level.
// Hierarchy: country(0) → group(1) → division(2) → site(3) → dvm(4)
export function buildPartialScope(
  scope: ScopeFilters,
  upToLevel: FilterState["drillLevel"]
): ScopeFilters {
  const idx = DRILL_LEVELS.indexOf(upToLevel);
  return {
    countryCode: idx >= 0 ? scope.countryCode : null,
    groupName: idx >= 1 ? scope.groupName : null,
    divisionName: idx >= 2 ? scope.divisionName : null,
    locationId: idx >= 3 ? scope.locationId : null,
    resourceId: null,
  };
}

// Which ancestor averages appear above the drill table: NVA/country always, and
// each deeper level only once that level is actually chosen in the scope --
// otherwise "Group Avg." would just restate the NVA row. Exported because the
// rollup path shows the same rows, and two copies of this predicate would drift
// into disagreeing about which averages exist, which reads as a data bug rather
// than as the UI rule it is.
export function pinnedAncestorLevels(
  drillLevel: FilterState["drillLevel"],
  scope: ScopeFilters
): FilterState["drillLevel"][] {
  return DRILL_LEVELS.slice(0, DRILL_LEVELS.indexOf(drillLevel)).filter(
    (level) =>
      level === "country" ||
      (level === "group" && scope.groupName) ||
      (level === "division" && scope.divisionName) ||
      (level === "site" && scope.locationId)
  );
}

export function ancestorLabel(level: FilterState["drillLevel"], scope: ScopeFilters): string {
  switch (level) {
    case "country":
      return scope.countryCode ? `${scope.countryCode} Avg.` : "NVA Avg. (Eligible Sites)";
    case "group":
      return scope.groupName ? `${scope.groupName} Avg.` : "Group Avg.";
    case "division":
      return scope.divisionName ? `${scope.divisionName} Avg.` : "Division Avg.";
    case "site":
      return "Site Avg.";
    case "dvm":
      return "DVM Avg.";
  }
}

export function useDrillData(
  rows: SchedulingRow[],
  filters: FilterState
): { rows: DrillRow[]; pinned: PinnedRow[] } {
  // Destructured outside the memo -- see useMetrics. A display-only toggle was
  // regrouping and recomputing every drill entity.
  const { customStart, customEnd, scope, appointmentTypes, drillLevel, excludeZeroApptDays } =
    filters;
  return useMemo(() => {
    const base = applyBaseFilter(rows, excludeZeroApptDays);
    const dated = filterRowsByDateRange(base, customStart, customEnd);
    // Peer scope, not the full scope: the drill level's own selection
    // highlights a row rather than filtering the list. See drillScope.ts.
    const scoped = filterRowsByScope(dated, peerScope(scope, drillLevel));

    const grouped = groupRows(scoped, drillLevel);
    const drillRows = Array.from(grouped.entries())
      .map(([id, { label, rows: gRows }]) => ({
        id,
        label,
        siteCount: new Set(gRows.map((r) => r.location_id)).size,
        metrics: computeMetrics(gRows, customStart, customEnd, appointmentTypes),
      }))
      .sort((a, b) => b.metrics.dvmHours - a.metrics.dvmHours);

    const pinned: PinnedRow[] = pinnedAncestorLevels(drillLevel, scope).map((level) => {
      const partialScope = buildPartialScope(scope, level);
      const ancestorRows = filterRowsByScope(dated, partialScope);
      return {
        label: ancestorLabel(level, scope),
        siteCount: new Set(ancestorRows.map((r) => r.location_id)).size,
        metrics: computeMetrics(ancestorRows, customStart, customEnd, appointmentTypes),
      };
    });

    return { rows: drillRows, pinned };
    // Specific fields, not the whole object -- see useMetrics. A display-only
    // toggle was regrouping and recomputing every drill entity.
  }, [rows, excludeZeroApptDays, customStart, customEnd, scope, appointmentTypes, drillLevel]);
}
